import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { IMAGES } from "@/data/images";

const LINKS = [
  { label: "Services", href: "/services" },
  { label: "Tips", href: "/tips" },
  { label: "Coverage", href: "#coverage" },
  { label: "Get A Quote", href: "#quote" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 350);
      } else {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b-2 border-[#0A1A4A]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
        <button onClick={() => go("/")} className="flex items-center gap-3" data-testid="nav-logo">
          <img src={IMAGES.logo} alt="FIT N FIX DIY Services Swansea" className="h-12 w-12 rounded-full border-2 border-[#0A1A4A]" />
          <span className="hidden sm:block font-display font-extrabold text-[#0A1A4A] text-lg tracking-tight leading-none text-left">
            FIT N FIX<span className="block font-mono-accent text-[10px] tracking-widest text-[#2563EB] font-normal">DIY SERVICES</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-mono-accent text-xs tracking-widest uppercase text-[#0A1A4A] hover:text-[#2563EB] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#quote")}
            data-testid="nav-cta"
            className="bg-[#2563EB] text-white font-display font-extrabold text-sm uppercase tracking-wide px-6 py-3 border-2 border-[#0A1A4A] shadow-hard hover:bg-[#0A1A4A] transition-colors"
          >
            Get A Quote
          </button>
        </div>

        <button className="md:hidden text-[#0A1A4A]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="md:hidden bg-white border-b-2 border-[#0A1A4A] px-6 py-4 flex flex-col gap-4"
        >
          {LINKS.map((l) => (
            <button key={l.href} onClick={() => go(l.href)} className="text-left font-mono-accent text-sm tracking-widest uppercase text-[#0A1A4A]">
              {l.label}
            </button>
          ))}
          <button onClick={() => go("#quote")} className="bg-[#2563EB] text-white font-display font-extrabold text-sm uppercase py-3 border-2 border-[#0A1A4A]">
            Get A Quote
          </button>
        </motion.div>
      )}
    </motion.header>
  );
};
